import supabase from '../supabase/supabase';
import { compressImage } from './compressImage';

const PROOF_BUCKET = 'zevi-payment-proofs-20260925';
const LOOKUP_STORAGE_KEY = 'zevi-order-lookups';

function safeExtension(file) {
  const extensions = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp'
  };
  return extensions[file.type] || null;
}

export async function getCheckoutConfiguration() {
  const [paymentResult, shippingResult, settingsResult] = await Promise.all([
    supabase
      .from('zevi_payment_settings_20260925')
      .select(`
        id,
        method,
        enabled,
        customer_instructions,
        qr_storage_path,
        qr_image_url,
        max_proof_size_bytes,
        accepted_mime_types
      `)
      .eq('enabled', true),
    supabase
      .from('zevi_shipping_methods_20260925')
      .select(`
        id,
        name,
        description,
        zevi_shipping_rates_20260925(
          id,
          region,
          rate,
          minimum_subtotal,
          maximum_subtotal,
          active
        )
      `)
      .eq('active', true)
      .order('sort_order'),
    supabase
      .from('zevi_site_settings_20260925')
      .select('setting_key, setting_value')
      .eq('visibility', 'public')
  ]);

  if (paymentResult.error || shippingResult.error || settingsResult.error) {
    throw new Error('Checkout configuration is temporarily unavailable.');
  }

  const paymentMethods = paymentResult.data || [];
  for (const method of paymentMethods) {
    if (method.method === 'qr_payment' && !method.qr_image_url && method.qr_storage_path) {
      const { data } = supabase.storage
        .from('zevi-qr-assets-20260925')
        .getPublicUrl(method.qr_storage_path);
      method.qr_image_url = data?.publicUrl || null;
    }
  }

  const deliveryRates = (shippingResult.data || []).flatMap((method) =>
    (method.zevi_shipping_rates_20260925 || [])
      .filter((rate) => rate.active !== false)
      .map((rate) => ({
        ...rate,
        shipping_method_id: method.id,
        shipping_method_name: method.name
      }))
  );

  // Parse store pickup settings
  const allSettings = Object.fromEntries(
    (settingsResult.data || []).map((setting) => [
      setting.setting_key,
      setting.setting_value
    ])
  );

  const storePickup = {
    enabled: allSettings.store_pickup_enabled?.enabled || false,
    address: allSettings.store_pickup_address?.address || '',
    city: allSettings.store_pickup_address?.city || '',
    phone: allSettings.store_pickup_address?.phone || '',
    hours: allSettings.store_pickup_address?.hours || ''
  };

  return {
    paymentMethods,
    shippingMethods: shippingResult.data || [],
    deliveryRates,
    storePickup,
    settings: allSettings
  };
}

export function validateProof(file, paymentSetting) {
  if (!file) return 'Payment proof is required.';
  const acceptedTypes = paymentSetting?.accepted_mime_types || [
    'image/jpeg',
    'image/png',
    'image/webp'
  ];
  const maximumSize = paymentSetting?.max_proof_size_bytes || 5242880;

  if (!acceptedTypes.includes(file.type) || !safeExtension(file)) {
    return 'Upload a JPG, PNG, or WebP image.';
  }
  if (file.size > maximumSize) {
    return `The proof must be smaller than ${Math.round(maximumSize / 1048576)} MB.`;
  }
  return '';
}

export async function uploadGuestProof(file) {
  file = await compressImage(file);
  const extension = safeExtension(file);
  if (!extension) {
    throw new Error('This payment-proof file type is not supported.');
  }

  const safeName = `guest/${crypto.randomUUID()}.${extension}`;
  const { error } = await supabase.storage
    .from(PROOF_BUCKET)
    .upload(safeName, file, {
      cacheControl: '0',
      contentType: file.type,
      upsert: false
    });

  if (error) {
    console.error('Payment proof Storage upload failed:', error);
    throw new Error(`The payment proof could not be uploaded: ${error.message || 'Unknown Storage error'}`);
  }

  return {
    storagePath: safeName,
    originalFilename: file.name,
    mimeType: file.type,
    fileSize: file.size
  };
}

export async function submitGuestProof(orderNumber, lookupToken, proofData) {
  const { data, error } = await supabase.rpc(
    'zevi_submit_guest_proof_20260925',
    {
      input_order_number: orderNumber,
      input_lookup_token: lookupToken,
      input_storage_path: proofData.storagePath,
      input_original_filename: proofData.originalFilename,
      input_mime_type: proofData.mimeType,
      input_file_size: proofData.fileSize
    }
  );
  if (error) {
    throw new Error(error.message || 'Payment proof could not be submitted.');
  }
  return data;
}

export async function createOrder(payload, idempotencyKey) {
  const { data, error } = await supabase.rpc(
    'zevi_create_order_20260925',
    {
      input_data: payload,
      input_idempotency_key: idempotencyKey
    }
  );
  if (error) {
    throw new Error(error.message || 'Your order could not be created.');
  }

  if (data?.lookup_token) {
    const storedLookups = JSON.parse(
      localStorage.getItem(LOOKUP_STORAGE_KEY) || '{}'
    );
    storedLookups[data.order_number] = data.lookup_token;
    localStorage.setItem(LOOKUP_STORAGE_KEY, JSON.stringify(storedLookups));
  }
  return data;
}

export async function lookupOrder(orderNumber, lookupToken) {
  const stored = JSON.parse(
    localStorage.getItem(LOOKUP_STORAGE_KEY) || '{}'
  );
  const token = lookupToken || stored[orderNumber];
  if (!token) {
    throw new Error('Secure order verification is required.');
  }

  const { data, error } = await supabase.rpc(
    'zevi_lookup_order_20260925',
    {
      input_order_number: orderNumber,
      input_lookup_token: token
    }
  );

  if (error) {
    // If the lookup function doesn't exist or digest() is unavailable
    // (migration not applied), return a friendly processing status
    const isMissingFn =
      error.code === 'PGRST202' ||
      error.code === '42883' ||
      /could not find the function/i.test(error.message || '') ||
      /does not exist/i.test(error.message || '');

    if (isMissingFn) {
      return {
        order_number: orderNumber,
        order_status: 'processing',
        payment_status: 'pending',
        payment_method: '',
        grand_total: 0,
        created_at: new Date().toISOString(),
        items: [],
        _lookup_unavailable: true
      };
    }

    throw new Error('The order details could not be verified.');
  }

  if (!data) {
    throw new Error('No order was found with this number.');
  }

  return data;
}